import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, createEventDynamicLink } from '../firebase/firebase';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import populateFirestore from '../scripts/populateFirestore';
import createMultilingualMenu from '../scripts/createMultilingualMenu';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const Admin = () => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const { t } = useTranslation();

  // Event form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dynamicLink, setDynamicLink] = useState('');

  // Multilingual menu state
  const [multilingualMenuLoading, setMultilingualMenuLoading] = useState(false);
  const [multilingualMenuError, setMultilingualMenuError] = useState('');
  const [multilingualMenuSuccess, setMultilingualMenuSuccess] = useState('');

  // Menu population state
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [menuSuccess, setMenuSuccess] = useState('');

  // Menu management state
  const [menuData, setMenuData] = useState(null);
  const [fetchingMenu, setFetchingMenu] = useState(false);
  const [menuFetchError, setMenuFetchError] = useState('');

  // Menu item form state
  const [selectedSection, setSelectedSection] = useState('');
  const [itemId, setItemId] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [itemError, setItemError] = useState('');
  const [itemSuccess, setItemSuccess] = useState('');

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editItemId, setEditItemId] = useState('');
  const [editSectionTitle, setEditSectionTitle] = useState('');

  // Fetch menu data when component mounts
  useEffect(() => {
    fetchMenuData();
  }, [currentLanguage]);

  // Function to fetch menu data from Firestore
  const fetchMenuData = async () => {
    try {
      setFetchingMenu(true);
      setMenuFetchError('');

      // First try to fetch from menu_translations collection
      const translationsCollection = collection(db, 'menu_translations');
      const translationsSnapshot = await getDocs(translationsCollection);

      if (!translationsSnapshot.empty) {
        // Get the first document from the collection
        const translationsDoc = translationsSnapshot.docs[0];
        const multilingualData = translationsDoc.data();

        // Create a menu object from the multilingual data using the current language
        const menuData = {
          id: translationsDoc.id,
          collectionName: 'menu_translations',
          isMultilingual: true,
          originalData: multilingualData,
          title: multilingualData.translations[currentLanguage]?.title || multilingualData.originalTitle,
          subtitle: multilingualData.translations[currentLanguage]?.subtitle || multilingualData.originalSubtitle,
          date: multilingualData.translations[currentLanguage]?.date || multilingualData.originalDate,
          footer: multilingualData.translations[currentLanguage]?.footer || multilingualData.originalFooter,
          sections: multilingualData.sections.map(section => ({
            title: section.translations[currentLanguage]?.title || section.id,
            originalId: section.id,
            courses: section.courses.map(course => ({
              id: course.id,
              name: course.translations[currentLanguage]?.name || course.id,
              description: course.translations[currentLanguage]?.description || ''
            }))
          }))
        };

        setMenuData(menuData);
      } else {
        // If no multilingual menu found, try the menu_2025Now collection
        const menu2025Collection = collection(db, 'menu_2025Now');
        const menu2025Snapshot = await getDocs(menu2025Collection);

        if (!menu2025Snapshot.empty) {
          // Get the first document from the collection
          const menuDoc = menu2025Snapshot.docs[0];
          const data = menuDoc.data();
          setMenuData({
            id: menuDoc.id,
            collectionName: 'menu_2025Now',
            isMultilingual: false,
            ...data
          });
        } else {
          // If no menu found in menu_2025Now, try the regular menu collection
          const menuCollection = collection(db, 'menu');
          const menuSnapshot = await getDocs(menuCollection);

          if (!menuSnapshot.empty) {
            // Get the first document from the collection
            const menuDoc = menuSnapshot.docs[0];
            const data = menuDoc.data();
            setMenuData({
              id: menuDoc.id,
              collectionName: 'menu',
              isMultilingual: false,
              ...data
            });
          } else {
            setMenuFetchError('No menu data found in any collection. Please use the "Check Menu Data Status" button or create a multilingual menu.');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching menu data:', err);
      setMenuFetchError(`Error fetching menu data: ${err.message}. Make sure the menu collections exist in Firestore.`);
    } finally {
      setFetchingMenu(false);
    }
  };

  // Function to add a new menu item
  const handleAddMenuItem = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!selectedSection || !itemId || !itemName || !itemDescription) {
      setItemError('Please fill in all fields');
      return;
    }

    try {
      setAddingItem(true);
      setItemError('');
      setItemSuccess('');

      if (!menuData) {
        setItemError('No menu data available. Please upload a menu first.');
        return;
      }

      // Check if section exists
      const sectionIndex = menuData.sections.findIndex(section => section.title === selectedSection);
      if (sectionIndex === -1) {
        setItemError(`Section "${selectedSection}" not found in the menu.`);
        return;
      }

      // Check if item ID already exists in the section
      const itemExists = menuData.sections[sectionIndex].courses.some(course => course.id === itemId);
      if (itemExists && !editMode) {
        setItemError(`An item with ID "${itemId}" already exists in this section.`);
        return;
      }

      // Handle differently based on whether we're using multilingual menu or not
      if (menuData.isMultilingual) {
        // Handle multilingual menu
        await handleMultilingualMenuItem(sectionIndex);
      } else {
        // Handle regular menu
        await handleRegularMenuItem(sectionIndex);
      }

      // Reset form
      setItemId('');
      setItemName('');
      setItemDescription('');
      setItemSuccess(editMode ? 'Item updated successfully!' : 'Item added successfully!');

      // Exit edit mode if applicable
      if (editMode) {
        setEditMode(false);
        setEditItemId('');
        setEditSectionTitle('');
      }

    } catch (err) {
      console.error('Error adding/updating menu item:', err);
      setItemError(`Error adding/updating menu item: ${err.message}. Make sure the menu collection exists and you have the correct permissions.`);
    } finally {
      setAddingItem(false);
    }
  };

  // Helper function to handle adding/updating items in a regular menu
  const handleRegularMenuItem = async (sectionIndex) => {
    // Create a copy of the menu data
    const updatedMenuData = { ...menuData };

    if (editMode) {
      // Update existing item
      const courseIndex = updatedMenuData.sections[sectionIndex].courses.findIndex(course => course.id === editItemId);
      if (courseIndex === -1) {
        throw new Error(`Item with ID "${editItemId}" not found in section "${selectedSection}".`);
      }

      updatedMenuData.sections[sectionIndex].courses[courseIndex] = {
        id: itemId,
        name: itemName,
        description: itemDescription
      };

      // If the section changed, remove from old section and add to new section
      if (editSectionTitle !== selectedSection) {
        const oldSectionIndex = updatedMenuData.sections.findIndex(section => section.title === editSectionTitle);
        if (oldSectionIndex !== -1) {
          // Remove from old section
          updatedMenuData.sections[oldSectionIndex].courses = updatedMenuData.sections[oldSectionIndex].courses.filter(
            course => course.id !== editItemId
          );

          // Add to new section
          updatedMenuData.sections[sectionIndex].courses.push({
            id: itemId,
            name: itemName,
            description: itemDescription
          });
        }
      }
    } else {
      // Add new item
      updatedMenuData.sections[sectionIndex].courses.push({
        id: itemId,
        name: itemName,
        description: itemDescription
      });
    }

    // Update the menu document in Firestore
    const menuDocRef = doc(db, menuData.collectionName, menuData.id);
    await updateDoc(menuDocRef, {
      sections: updatedMenuData.sections
    });

    // Update local state
    setMenuData(updatedMenuData);
  };

  // Helper function to handle adding/updating items in a multilingual menu
  const handleMultilingualMenuItem = async (sectionIndex) => {
    // Get the original multilingual data
    const multilingualData = menuData.originalData;

    // Find the section in the original data
    const originalSectionId = menuData.sections[sectionIndex].originalId;
    const originalSectionIndex = multilingualData.sections.findIndex(section => section.id === originalSectionId);

    if (originalSectionIndex === -1) {
      throw new Error(`Section with ID "${originalSectionId}" not found in the multilingual menu.`);
    }

    // Create a copy of the multilingual data
    const updatedMultilingualData = { ...multilingualData };

    if (editMode) {
      // Update existing item
      const courseIndex = updatedMultilingualData.sections[originalSectionIndex].courses.findIndex(course => course.id === editItemId);
      if (courseIndex === -1) {
        throw new Error(`Item with ID "${editItemId}" not found in section "${selectedSection}".`);
      }

      // Update the translation for the current language
      updatedMultilingualData.sections[originalSectionIndex].courses[courseIndex].translations[currentLanguage] = {
        name: itemName,
        description: itemDescription
      };

      // If the section changed, move the item to the new section
      if (editSectionTitle !== selectedSection) {
        // Find the new section in the original data
        const newSectionTitle = selectedSection;
        const newSectionIndex = menuData.sections.findIndex(section => section.title === newSectionTitle);
        if (newSectionIndex === -1) {
          throw new Error(`Section "${newSectionTitle}" not found in the menu.`);
        }

        const newOriginalSectionId = menuData.sections[newSectionIndex].originalId;
        const newOriginalSectionIndex = updatedMultilingualData.sections.findIndex(section => section.id === newOriginalSectionId);

        if (newOriginalSectionIndex === -1) {
          throw new Error(`Section with ID "${newOriginalSectionId}" not found in the multilingual menu.`);
        }

        // Get the item to move
        const itemToMove = updatedMultilingualData.sections[originalSectionIndex].courses[courseIndex];

        // Remove from old section
        updatedMultilingualData.sections[originalSectionIndex].courses = updatedMultilingualData.sections[originalSectionIndex].courses.filter(
          course => course.id !== editItemId
        );

        // Add to new section
        updatedMultilingualData.sections[newOriginalSectionIndex].courses.push(itemToMove);
      }
    } else {
      // Add new item
      updatedMultilingualData.sections[originalSectionIndex].courses.push({
        id: itemId,
        translations: {
          en: {
            name: currentLanguage === 'en' ? itemName : itemId,
            description: currentLanguage === 'en' ? itemDescription : ''
          },
          fr: {
            name: currentLanguage === 'fr' ? itemName : itemId,
            description: currentLanguage === 'fr' ? itemDescription : ''
          },
          es: {
            name: currentLanguage === 'es' ? itemName : itemId,
            description: currentLanguage === 'es' ? itemDescription : ''
          },
          tr: {
            name: currentLanguage === 'tr' ? itemName : itemId,
            description: currentLanguage === 'tr' ? itemDescription : ''
          }
        }
      });
    }

    // Update the menu document in Firestore
    const menuDocRef = doc(db, 'menu_translations', menuData.id);
    await updateDoc(menuDocRef, updatedMultilingualData);

    // Fetch the updated menu data to refresh the UI
    await fetchMenuData();
  };

  // Function to delete a menu item
  const handleDeleteMenuItem = async (sectionTitle, itemId) => {
    if (!window.confirm(`Are you sure you want to delete this item?`)) {
      return;
    }

    try {
      setItemError('');

      if (!menuData) {
        setItemError('No menu data available.');
        return;
      }

      // Find the section
      const sectionIndex = menuData.sections.findIndex(section => section.title === sectionTitle);
      if (sectionIndex === -1) {
        setItemError(`Section "${sectionTitle}" not found in the menu.`);
        return;
      }

      // Check if item exists
      const itemIndex = menuData.sections[sectionIndex].courses.findIndex(course => course.id === itemId);
      if (itemIndex === -1) {
        setItemError(`Item with ID "${itemId}" not found in section "${sectionTitle}".`);
        return;
      }

      // Handle differently based on whether we're using multilingual menu or not
      if (menuData.isMultilingual) {
        // Handle multilingual menu
        await handleDeleteMultilingualMenuItem(sectionTitle, sectionIndex, itemId);
      } else {
        // Handle regular menu
        await handleDeleteRegularMenuItem(sectionIndex, itemIndex);
      }

      setItemSuccess('Item deleted successfully!');

    } catch (err) {
      console.error('Error deleting menu item:', err);
      setItemError(`Error deleting menu item: ${err.message}. Make sure the menu collection exists and you have the correct permissions.`);
    }
  };

  // Helper function to handle deleting items in a regular menu
  const handleDeleteRegularMenuItem = async (sectionIndex, itemIndex) => {
    // Create a copy of the menu data
    const updatedMenuData = { ...menuData };

    // Remove the item
    updatedMenuData.sections[sectionIndex].courses.splice(itemIndex, 1);

    // Update the menu document in Firestore
    const menuDocRef = doc(db, menuData.collectionName, menuData.id);
    await updateDoc(menuDocRef, {
      sections: updatedMenuData.sections
    });

    // Update local state
    setMenuData(updatedMenuData);
  };

  // Helper function to handle deleting items in a multilingual menu
  const handleDeleteMultilingualMenuItem = async (sectionTitle, sectionIndex, itemId) => {
    // Get the original multilingual data
    const multilingualData = menuData.originalData;

    // Find the section in the original data
    const originalSectionId = menuData.sections[sectionIndex].originalId;
    const originalSectionIndex = multilingualData.sections.findIndex(section => section.id === originalSectionId);

    if (originalSectionIndex === -1) {
      throw new Error(`Section with ID "${originalSectionId}" not found in the multilingual menu.`);
    }

    // Find the item in the original data
    const courseIndex = multilingualData.sections[originalSectionIndex].courses.findIndex(course => course.id === itemId);
    if (courseIndex === -1) {
      throw new Error(`Item with ID "${itemId}" not found in section "${sectionTitle}" in the multilingual menu.`);
    }

    // Create a copy of the multilingual data
    const updatedMultilingualData = { ...multilingualData };

    // Remove the item
    updatedMultilingualData.sections[originalSectionIndex].courses.splice(courseIndex, 1);

    // Update the menu document in Firestore
    const menuDocRef = doc(db, 'menu_translations', menuData.id);
    await updateDoc(menuDocRef, updatedMultilingualData);

    // Fetch the updated menu data to refresh the UI
    await fetchMenuData();
  };

  // Function to set up edit mode
  const handleEditMenuItem = (sectionTitle, item) => {
    setEditMode(true);
    setEditItemId(item.id);
    setEditSectionTitle(sectionTitle);
    setSelectedSection(sectionTitle);
    setItemId(item.id);
    setItemName(item.name);
    setItemDescription(item.description);

    // Scroll to the form
    document.getElementById('menuItemForm').scrollIntoView({ behavior: 'smooth' });
  };

  // Function to display the current language status in the form
  const getLanguageStatusText = () => {
    if (!menuData?.isMultilingual) {
      return '';
    }

    const languageNames = {
      en: 'English',
      fr: 'French',
      es: 'Spanish',
      tr: 'Turkish'
    };

    return `You are currently editing the ${languageNames[currentLanguage] || currentLanguage} version of this menu item.`;
  };

  // Function to cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditItemId('');
    setEditSectionTitle('');
    setSelectedSection('');
    setItemId('');
    setItemName('');
    setItemDescription('');
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  // Function to handle menu initialization
  const handlePopulateMenu = async () => {
    try {
      setMenuLoading(true);
      setMenuError('');
      setMenuSuccess('');

      const result = await populateFirestore();

      if (result.success) {
        // We no longer populate from local data
        setMenuSuccess('Menu data can only be managed through the Admin panel. Please use the form below to add menu items.');

        // Fetch the menu data to update the UI
        await fetchMenuData();
      } else {
        setMenuError('Failed to check menu data in Firestore.');
      }
    } catch (err) {
      console.error('Error checking menu data:', err);
      setMenuError(`Error: ${err.message}`);
    } finally {
      setMenuLoading(false);
    }
  };

  // Function to handle creating multilingual menu
  const handleCreateMultilingualMenu = async () => {
    try {
      setMultilingualMenuLoading(true);
      setMultilingualMenuError('');
      setMultilingualMenuSuccess('');

      const result = await createMultilingualMenu();

      if (result.success) {
        setMultilingualMenuSuccess(result.message);

        // Fetch the menu data to update the UI
        await fetchMenuData();
      } else {
        setMultilingualMenuError(result.message);
      }
    } catch (err) {
      console.error('Error creating multilingual menu:', err);
      setMultilingualMenuError(`Error: ${err.message}`);
    } finally {
      setMultilingualMenuLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !description || !date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const slug = generateSlug(title);
      let imageUrl = '';

      // Upload image if provided
      if (image) {
        const storageRef = ref(storage, `events/${slug}-${Date.now()}`);
        await uploadBytes(storageRef, image);
        imageUrl = await getDownloadURL(storageRef);
      }

      // Create event document
      const eventData = {
        title,
        description,
        date,
        image: imageUrl,
        slug,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'events'), eventData);

      // Generate dynamic link
      const link = await createEventDynamicLink(slug);
      setDynamicLink(link);

      setSuccess(`Event created successfully! ID: ${docRef.id}`);

      // Reset form
      setTitle('');
      setDescription('');
      setDate('');
      setImage(null);

    } catch (err) {
      console.error('Error creating event:', err);
      setError('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-bg">
      <Navbar />

      <div className="container-custom py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-text-dark mb-8">Admin Dashboard</h1>

          <div className="bg-white border border-sandy-beige/30 rounded-lg p-6 md:p-8 mb-8 shadow-md">
            <h2 className="text-xl font-heading font-bold text-med-terracotta mb-6">Create New Event</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-text-dark mb-2">Event Title</label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  placeholder="Enter event title"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-text-dark mb-2">Description</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="5"
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  placeholder="Enter event description"
                ></textarea>
              </div>

              <div>
                <label htmlFor="date" className="block text-text-dark mb-2">Event Date</label>
                <input
                  type="datetime-local"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                />
              </div>

              <div>
                <label htmlFor="image" className="block text-text-dark mb-2">Event Image</label>
                <input
                  type="file"
                  id="image"
                  onChange={handleImageChange}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  accept="image/*"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 rounded-full font-medium transition-all duration-300 inline-block bg-warm-terracotta text-white hover:opacity-90 hover:scale-105 shadow-md w-full"
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg text-green-700">
                {success}
              </div>
            )}

            {dynamicLink && (
              <div className="mt-6 p-4 bg-light-bg-alt border border-sandy-beige/50 rounded-lg">
                <h3 className="text-lg font-heading font-bold text-med-terracotta mb-2">Dynamic Link Created</h3>
                <p className="text-text-dark break-all mb-2">{dynamicLink}</p>
                <button
                  onClick={() => navigator.clipboard.writeText(dynamicLink)}
                  className="text-sm text-med-blue hover:underline"
                >
                  Copy to clipboard
                </button>
              </div>
            )}
          </div>

          {/* Menu Data Management Section */}
          <div className="bg-light-bg border border-sandy-beige/30 rounded-lg p-6 md:p-8 mb-8">
            <h2 className="text-xl font-heading font-bold text-med-terracotta mb-6">Menu Data Management</h2>

            <div className="space-y-4">
              <p className="text-text-dark">
                Use this section to check the status of the menu data in the Firestore database.
                <br/><br/>
                <strong>Note:</strong> All menu changes should be made through the Admin panel only. The menu data is no longer initialized from local data to prevent accidental overwrites during deployment.
              </p>

              <div>
                <button
                  onClick={handlePopulateMenu}
                  disabled={menuLoading}
                  className="px-6 py-2 rounded-full font-medium transition-all duration-300 inline-block bg-warm-terracotta text-white hover:opacity-90 hover:scale-105 shadow-md w-full md:w-auto"
                >
                  {menuLoading ? 'Checking Menu Data...' : 'Check Menu Data Status'}
                </button>
              </div>

              {menuError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                  {menuError}
                </div>
              )}

              {menuSuccess && (
                <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg text-green-700">
                  {menuSuccess}
                </div>
              )}
            </div>
          </div>

          {/* Multilingual Menu Management Section */}
          <div className="bg-light-bg border border-sandy-beige/30 rounded-lg p-6 md:p-8 mb-8">
            <h2 className="text-xl font-heading font-bold text-med-terracotta mb-6">Multilingual Menu Management</h2>

            <div className="space-y-4">
              <p className="text-text-dark">
                Use this section to create and manage multilingual menu data. This will create a new collection in Firestore that supports multiple languages.
                <br/><br/>
                <strong>Current Language:</strong> {
                  currentLanguage === 'en' ? 'English' :
                  currentLanguage === 'fr' ? 'French' :
                  currentLanguage === 'es' ? 'Spanish' :
                  currentLanguage === 'tr' ? 'Turkish' :
                  currentLanguage
                }
              </p>

              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={() => changeLanguage(languages.EN)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    currentLanguage === languages.EN 
                      ? 'bg-warm-terracotta text-white' 
                      : 'bg-gray-100 text-text-dark hover:bg-gray-200'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage(languages.FR)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    currentLanguage === languages.FR 
                      ? 'bg-warm-terracotta text-white' 
                      : 'bg-gray-100 text-text-dark hover:bg-gray-200'
                  }`}
                >
                  French
                </button>
                <button
                  onClick={() => changeLanguage(languages.ES)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    currentLanguage === languages.ES 
                      ? 'bg-warm-terracotta text-white' 
                      : 'bg-gray-100 text-text-dark hover:bg-gray-200'
                  }`}
                >
                  Spanish
                </button>
                <button
                  onClick={() => changeLanguage(languages.TR)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    currentLanguage === languages.TR 
                      ? 'bg-warm-terracotta text-white' 
                      : 'bg-gray-100 text-text-dark hover:bg-gray-200'
                  }`}
                >
                  Turkish
                </button>
              </div>

              <div>
                <button
                  onClick={handleCreateMultilingualMenu}
                  disabled={multilingualMenuLoading}
                  className="px-6 py-2 rounded-full font-medium transition-all duration-300 inline-block bg-warm-terracotta text-white hover:opacity-90 hover:scale-105 shadow-md w-full md:w-auto"
                >
                  {multilingualMenuLoading ? 'Creating Multilingual Menu...' : 'Create Multilingual Menu'}
                </button>
              </div>

              {multilingualMenuError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                  {multilingualMenuError}
                </div>
              )}

              {multilingualMenuSuccess && (
                <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg text-green-700">
                  {multilingualMenuSuccess}
                </div>
              )}
            </div>
          </div>

          {/* Menu Item Management Section */}
          <div className="bg-white border border-sandy-beige/30 rounded-lg p-6 md:p-8 mb-8 shadow-md">
            <h2 className="text-xl font-heading font-bold text-med-terracotta mb-6">
              {editMode ? 'Edit Menu Item' : 'Add New Menu Item'}
            </h2>

            {menuData?.isMultilingual && (
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
                <p className="text-sm">{getLanguageStatusText()}</p>
                <p className="text-xs mt-1">Switch languages in the Multilingual Menu Management section to edit content in different languages.</p>
              </div>
            )}

            {/* Menu Item Form */}
            <form id="menuItemForm" onSubmit={handleAddMenuItem} className="space-y-6">
              <div>
                <label htmlFor="section" className="block text-text-dark mb-2">Section</label>
                <select
                  id="section"
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  disabled={fetchingMenu || !menuData}
                >
                  <option value="">Select a section</option>
                  {menuData?.sections?.map((section) => (
                    <option key={section.title} value={section.title}>
                      {section.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="itemId" className="block text-text-dark mb-2">Item ID</label>
                <input
                  type="text"
                  id="itemId"
                  value={itemId}
                  onChange={(e) => setItemId(e.target.value)}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  placeholder="Enter a unique ID (e.g., 'espresso-martini')"
                  disabled={fetchingMenu || !menuData}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Use lowercase letters, numbers, and hyphens only. This ID must be unique within the section.
                </p>
              </div>

              <div>
                <label htmlFor="itemName" className="block text-text-dark mb-2">Item Name</label>
                <input
                  type="text"
                  id="itemName"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  placeholder="Enter item name (e.g., 'Espresso Martini')"
                  disabled={fetchingMenu || !menuData}
                />
              </div>

              <div>
                <label htmlFor="itemDescription" className="block text-text-dark mb-2">Description</label>
                <textarea
                  id="itemDescription"
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  rows="4"
                  className="w-full bg-light-bg-alt border border-sandy-beige/50 rounded-lg px-4 py-2 text-text-dark focus:outline-none focus:ring-2 focus:ring-warm-terracotta/50"
                  placeholder="Enter item description"
                  disabled={fetchingMenu || !menuData}
                ></textarea>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={addingItem || fetchingMenu || !menuData}
                  className="px-6 py-2 rounded-full font-medium transition-all duration-300 inline-block bg-warm-terracotta text-white hover:opacity-90 hover:scale-105 shadow-md"
                >
                  {addingItem
                    ? (editMode ? 'Updating...' : 'Adding...')
                    : (editMode ? 'Update Item' : 'Add Item')}
                </button>

                {editMode && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-6 py-2 rounded-full font-medium transition-all duration-300 inline-block border border-med-terracotta text-med-terracotta hover:bg-med-terracotta/10"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>

            {itemError && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                {itemError}
              </div>
            )}

            {itemSuccess && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg text-green-700">
                {itemSuccess}
              </div>
            )}

            {/* Loading indicator */}
            {fetchingMenu && (
              <div className="flex justify-center items-center h-20 mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-warm-terracotta"></div>
              </div>
            )}

            {/* Error message for menu fetch */}
            {menuFetchError && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                {menuFetchError}
              </div>
            )}
          </div>

          {/* Current Menu Items Section */}
          <div className="bg-white border border-sandy-beige/30 rounded-lg p-6 md:p-8 mb-8 shadow-md">
            <h2 className="text-xl font-heading font-bold text-med-terracotta mb-6">Current Menu Items</h2>

            {fetchingMenu ? (
              <div className="flex justify-center items-center h-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-warm-terracotta"></div>
              </div>
            ) : menuFetchError ? (
              <div className="p-3 bg-red-100 border border-red-400 rounded-lg text-red-700">
                {menuFetchError}
              </div>
            ) : !menuData ? (
              <div className="text-center py-8 text-text-dark">
                No menu data available. Please upload a menu first.
              </div>
            ) : (
              <div className="space-y-8">
                {menuData.sections.map((section) => (
                  <div key={section.title} className="border-b border-sandy-beige/30 pb-6 last:border-b-0 last:pb-0">
                    <h3 className="text-lg font-heading font-bold text-med-blue mb-4">{section.title}</h3>

                    {section.courses.length === 0 ? (
                      <p className="text-text-dark italic">No items in this section</p>
                    ) : (
                      <div className="space-y-4">
                        {section.courses.map((item) => (
                          <div key={item.id} className="bg-light-bg-alt p-4 rounded-lg border border-sandy-beige/30">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                              <div className="flex-grow">
                                <h4 className="font-heading font-bold text-text-dark">{item.name}</h4>
                                <p className="text-sm text-gray-500 mb-2">ID: {item.id}</p>
                                <p className="text-text-dark/80">{item.description}</p>
                              </div>
                              <div className="flex flex-row md:flex-col gap-2">
                                <button
                                  onClick={() => handleEditMenuItem(section.title, item)}
                                  className="px-4 py-1 text-sm rounded-full bg-med-blue text-white hover:bg-med-blue/90 transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteMenuItem(section.title, item.id)}
                                  className="px-4 py-1 text-sm rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;
